import { motion } from 'motion/react';
import { FC, useEffect, useRef, useState } from 'react';

const EXIT_DWELL_TIME = 1500;

interface BrainBreakCompletedProps {
  onExitComplete?: () => void;
}

export const BrainBreakCompleted: FC<BrainBreakCompletedProps> = ({ onExitComplete }) => {
  const [phase, setPhase] = useState<'enter' | 'exit'>('enter');
  const exitTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
      }
    };
  }, []);

  const handleEnterComplete = () => {
    exitTimeoutRef.current = setTimeout(() => {
      setPhase('exit');
    }, EXIT_DWELL_TIME);
  };

  const handleExitComplete = () => {
    onExitComplete?.();
  };

  return (
    <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full">
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={phase === 'exit' ? { y: 455, opacity: 0 } : { y: 0, opacity: 1 }}
        transition={
          phase === 'exit'
            ? {
                y: {
                  duration: 0.84,
                  ease: [0.75, 0.75, 0.25, 0.25],
                },
                opacity: {
                  duration: 0.22,
                  ease: 'easeOut',
                },
              }
            : {
                duration: 0.5,
                ease: [0.16, 1, 0.3, 1],
              }
        }
        onAnimationComplete={
          phase === 'enter' ? handleEnterComplete : phase === 'exit' ? handleExitComplete : undefined
        }
        className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12 tw-gap-8"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="tw-text-center tw-space-y-4 tw-max-w-md tw-w-full tw-relative"
        >
          <motion.h2
            className="tw-text-indigo-600 tw-font-semibold tw-text-2xl tw-m-0 tw-font-atkinson"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 15,
            }}
          >
            Thanks!
          </motion.h2>

          <motion.div
            aria-hidden
            style={{
              position: 'absolute',
              width: 15,
              height: 15,
              borderRadius: '50%',
              background: '#FCB814',
              left: 'calc(50% - 57px)',
              top: 'calc(50% - 9px)',
              pointerEvents: 'none',
            }}
            initial={{ opacity: 0, x: 0, y: 0 }}
            animate={{ opacity: [0, 1, 1, 0], x: [0, 0, -98], y: [0, 0, 33] }}
            transition={{
              duration: 0.8,
              times: [0, 0.16, 0.8, 1],
              ease: [0.25, 0.25, 0.75, 0.75] as const,
              delay: 0.2,
            }}
          />
          <motion.div
            aria-hidden
            style={{
              position: 'absolute',
              width: 40,
              height: 15,
              borderRadius: 17,
              background: '#36C2DB',
              left: 'calc(50% + 55px)',
              top: 'calc(50% - 23px)',
              pointerEvents: 'none',
              rotate: '-19deg',
            }}
            initial={{ opacity: 0, x: 0, y: 0 }}
            animate={{ opacity: [0, 1, 1, 0], x: [0, 0, 66], y: [0, 0, -86] }}
            transition={{
              duration: 0.8,
              times: [0, 0.16, 0.8, 1],
              ease: [0.25, 0.25, 0.75, 0.75] as const,
              delay: 0.2,
            }}
          />

          <motion.div
            aria-hidden
            style={{
              position: 'absolute',
              width: 42,
              height: 15,
              borderRadius: 17,
              background: '#EB6FA5',
              left: 'calc(50% - 67px)',
              top: 'calc(50% + 10px)',
              pointerEvents: 'none',
              rotate: '-33deg',
            }}
            initial={{ opacity: 0, x: 0, y: 0 }}
            animate={{ opacity: [0, 1, 1, 0], x: [0, 0, -66], y: [0, 0, 60] }}
            transition={{
              duration: 0.8,
              times: [0, 0.16, 0.8, 1],
              ease: [0.25, 0.25, 0.75, 0.75] as const,
              delay: 0.2,
            }}
          />
        </motion.div>
      </motion.div>
    </div>
  );
};
